import { useMemo, useState } from 'react';
import {
    Button,
    GenericError,
    Icon,
    ImageUploader,
    PageLoader,
    TextField,
    TextInput,
    Title,
    UserAvatar,
} from '../common';
import { useUserSettings } from './useUserSettings';
import Select from '../common/Select/Select';
import { useTranslation } from '../../hooks/translations/TranslationProvider';
import { COLOURS, languageOptions } from '../../utils/consts';
import { useAppSelector } from '../../store/hooks';

import './userSettings.css';
import Autocomplete from '../common/Autocomplete/Autocomplete';

const UserSettings: React.FC = () => {
    const auth = useAppSelector(state => state.auth);
    const { t } = useTranslation();

    const currentUser = auth.authData!;

    const {
        isGeoDataLoading,
        geoDataError,
        generateOptions,
        onCountryChange,
        onCityChange,
        onSubmitHandler,
        getCitiesHandler,
        formValues,
        errors,
        countryCode,
        currency,
        isLoading,
        error,
        uploadImageHandler,
        onLanguageChange,
    } = useUserSettings();

    const [selectedImage, setSelectedImage] = useState<string | null>(null);

    const isSubmitDisabled = useMemo(() => {
        return (
            formValues.country === auth.authData?.country.name &&
            formValues.city === auth.authData?.city.name &&
            formValues.language === auth.authData?.language
        );
    }, [formValues, auth.authData]);

    const onSubmit = (event: React.FormEvent) => {
        event.preventDefault();

        onSubmitHandler();
    };

    if (isLoading) {
        return <PageLoader />;
    }

    if (geoDataError) {
        return <GenericError error={error} />;
    }

    return (
        <div className="user-settings page-container">
            <div className="user-settings-header-wrapper">
                <div className="user-settings-header">
                    <UserAvatar
                        imageSrc={selectedImage || currentUser.avatarUrl}
                        size="xl"
                    />
                    <div>
                        <Title text={t('userSettings.title')} size="xl" />
                        <ImageUploader
                            onImageSelection={url => setSelectedImage(url)}
                            onUploadImage={uploadImageHandler}
                        />
                        {error && <TextField color="red">{error}</TextField>}
                    </div>
                </div>
                {(!auth.authData?.avatarUrl || selectedImage) && (
                    <div className="flex gap">
                        <Icon name="bx-error" color={COLOURS.RED} />
                        <TextField>{t('userSettings.proTip')}</TextField>
                    </div>
                )}
            </div>

            <div className="user-settings-content">
                <div className="user-settings-form">
                    <form onSubmit={onSubmit}>
                        <div className="user-settings-form-text">
                            <TextField>Username</TextField>
                            <TextField weight="bold">
                                {currentUser.username}
                            </TextField>
                        </div>
                        <Select
                            name="language"
                            options={languageOptions}
                            onSelect={onLanguageChange}
                            value={formValues.language || currentUser.language}
                            label={t('register.language')}
                            className="full-width"
                            error={errors.language}
                            isDisabled={auth.isLoading || isGeoDataLoading}
                        />
                        <Select
                            name="country"
                            options={generateOptions.countryOptions}
                            onSelect={onCountryChange}
                            value={countryCode as string}
                            label={t('common.country')}
                            className="full-width"
                            error={errors.country}
                            isDisabled={auth.isLoading || isGeoDataLoading}
                        />
                        <Autocomplete
                            name="city"
                            startSearch={getCitiesHandler}
                            isLoading={isGeoDataLoading}
                            error={geoDataError}
                            results={generateOptions.citiesOptions}
                            handleOptionClick={onCityChange}
                            placeholder="Search cities"
                            initialValue={formValues.city as string}
                            label={t('common.city')}
                            className="full-width"
                        />
                        <TextInput
                            label={t('common.currency')}
                            name="currency"
                            placeholder={t('common.currency')}
                            isDisabled
                            value={currency || currentUser.country.currency}
                        />
                        <Button
                            type="submit"
                            text={t('userSettings.saveSettings')}
                            variant="green"
                            size="md"
                            disabled={
                                isSubmitDisabled ||
                                auth.isLoading ||
                                isGeoDataLoading
                            }
                        />
                    </form>
                </div>
            </div>
        </div>
    );
};

export default UserSettings;
